/**
 * Sentry Utilities
 * 
 * Helper functions for working with Sentry throughout the application.
 * Provides convenient wrappers for common Sentry operations.
 */

import * as Sentry from '@sentry/react';

/**
 * Set the current authenticated user context
 * Call this after successful login
 */
export function setUser(user: {
  id: number;
  email: string;
  name?: string;
  companyId?: number;
  role?: string;
}) {
  
  const sentryUser = {
    id: user.id.toString(),
    email: user.email,
    username: user.name || user.email.split('@')[0], // Fallback to email username if name not provided
  };
  
  Sentry.setUser(sentryUser);

  // Add company ID as a tag for easy filtering in Sentry
  if (user.companyId !== null && user.companyId !== undefined) {
    Sentry.setTag('companyId', user.companyId.toString());
  }

  // Add additional user context
  Sentry.setContext('user_details', {
    companyId: user.companyId,
    role: user.role,
    name: user.name,
  });
  
}

/**
 * Clear user context (call on logout)
 */
export function clearUser() {
  Sentry.setUser(null);
}

/**
 * Capture an exception with additional context
 */
export function captureException(error: Error, context?: {
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  tags?: Record<string, string>;
  extra?: Record<string, any>;
}) {
  Sentry.captureException(error, {
    level: context?.level || 'error',
    tags: context?.tags,
    extra: context?.extra,
  });
}

/**
 * Capture a message with context
 */
export function captureMessage(message: string, context?: {
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  tags?: Record<string, string>;
  extra?: Record<string, any>;
}) {
  Sentry.captureMessage(message, {
    level: context?.level || 'info',
    tags: context?.tags,
    extra: context?.extra,
  });
}

/**
 * Add breadcrumb for tracking user actions
 */
export function addBreadcrumb(breadcrumb: {
  message: string;
  category?: string;
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  data?: Record<string, any>;
}) {
  Sentry.addBreadcrumb({
    message: breadcrumb.message,
    category: breadcrumb.category || 'user-action',
    level: breadcrumb.level || 'info',
    data: breadcrumb.data,
  });
}

/**
 * Track custom span for performance monitoring
 * 
 * @example
 * ```ts
 * await trackSpan(
 *   { op: 'http.client', name: 'Fetch User Data' },
 *   async () => {
 *     return await fetchUserData();
 *   }
 * );
 * ```
 */
export async function trackSpan<T>(
  spanOptions: { op: string; name: string; attributes?: Record<string, any> },
  callback: () => Promise<T>
): Promise<T> {
  return await Sentry.startSpan(
    {
      op: spanOptions.op,
      name: spanOptions.name,
      attributes: spanOptions.attributes,
    },
    async () => {
      return await callback();
    }
  );
}

/**
 * Track button click or user interaction
 */
export function trackInteraction(name: string, attributes?: Record<string, any>) {
  Sentry.startSpan(
    {
      op: 'ui.click',
      name,
      attributes,
    },
    () => {
      // Interaction tracked
    }
  );
}

/**
 * Add custom tags to the current scope
 */
export function setTags(tags: Record<string, string>) {
  Sentry.setTags(tags);
}

/**
 * Add custom context to the current scope
 */
export function setContext(key: string, context: Record<string, any>) {
  Sentry.setContext(key, context);
}

/**
 * Show the user feedback dialog
 * Allows users to provide feedback on errors
 */
export function showFeedbackDialog(eventId?: string) {
  Sentry.showReportDialog({ eventId });
}

export default Sentry;
